import axios from "axios";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../context/AuthContext";
import {
    Table,
    Typography,
    message,
    Spin,
    Tag,
    Modal,
    Input,
    Button,
    DatePicker,
    Row,
    Col,
    Card,
    Select,
    Timeline,
} from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { DownloadOutlined } from "@ant-design/icons";

const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

export default function OtApprovals() {
    const { authData, backUrl } = useContext(AuthContext);

    const [allowEdit, setAllowEdit] = useState(false);

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [actionType, setActionType] = useState("");
    const [remarks, setRemarks] = useState("");

    // Pagination state
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    // Filter states
    const [filters, setFilters] = useState({
        searchName: "",
        searchCode: "",
        dateFrom: "",
        dateTo: "",
        selectedStatus: "",
        selectedOrganisation: "",
        selectedDepartment: "",
        selectedDesignation: "",
    });

    // Dropdown lists
    const [organisationList, setOrganisationList] = useState([]);
    const [departmentList, setDepartmentList] = useState([]);
    const [designationList, setDesignationList] = useState([]);

    // Get current user's full name
    const getCurrentUserName = () => {
        if (!authData?.employee?.user) return "";
        const firstName = authData.employee.user.first_name || "";
        const lastName = authData.employee.user.last_name || "";
        return `${firstName} ${lastName}`.trim();
    };

    // Fetch Organisation List
    const fetchOrganisations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setOrganisationList(res.data.results);
        } catch (err) {
            console.log("Organisation fetch failed");
        }
    };

    // Fetch Departments by Org
    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${orgId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDepartmentList(res.data.results);
        } catch (err) {
            console.log("Department fetch failed");
        }
    };

    // Fetch Designations by Dept
    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${deptId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDesignationList(res.data.results);
        } catch (err) {
            console.log("Designation fetch failed");
        }
    };

    // Build query params from filters (excluding status filter)
    const buildQueryParams = (page = 1, pageSize = 10) => {
        const params = new URLSearchParams();

        params.append('page', page);
        // params.append('page_size', pageSize);

        if (filters.searchName.trim()) {
            params.append('employee_name', filters.searchName.trim());
        }

        if (filters.searchCode.trim()) {
            params.append('employee_code', filters.searchCode.trim());
        }

        if (filters.dateFrom) {
            params.append('from_date', filters.dateFrom);
        }

        if (filters.dateTo) {
            params.append('to_date', filters.dateTo);
        }

        // Status filter removed from API call - will be handled client-side

        if (filters.selectedOrganisation) {
            params.append('organisation', filters.selectedOrganisation);
        }

        if (filters.selectedDepartment) {
            params.append('department', filters.selectedDepartment);
        }

        if (filters.selectedDesignation) {
            params.append('designation', filters.selectedDesignation);
        }

        return params.toString();
    };

    // Filter data based on current user's approval status
    const applyClientSideFilters = (rawData) => {
        if (!filters.selectedStatus) {
            return rawData;
        }

        const currentUserId = authData?.employee?.id;

        return rawData.filter((record) => {
            // Find the approval log that matches the current user by approver_id
            const userApprovalLog = (record.approval_logs || []).find((log) => {
                return log.approver === currentUserId;
            });

            // If no matching approval log found, exclude this record
            if (!userApprovalLog) {
                return false;
            }

            // Check if the user's approval status matches the filter
            const logStatus = userApprovalLog.status?.toLowerCase();
            const filterStatus = filters.selectedStatus.toLowerCase();

            return logStatus === filterStatus;
        });
    };

    // Fetch OT data with filters
    const fetchData = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const queryString = buildQueryParams(page, pageSize);
            const res = await axios.get(
                `${backUrl}/api/payroll/manager/ot-requests/?${queryString}`,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );

            const resultData = res.data.results || res.data;

            // Store raw data
            setData(resultData);

            // Apply client-side filtering for status
            const filtered = applyClientSideFilters(resultData);
            setFilteredData(filtered);

            const totalCount = res.data.count || resultData.length;

            setPagination({
                current: page,
                pageSize: pageSize,
                total: filtered.length, // Use filtered count for pagination
            });
        } catch (err) {
            message.error("Failed to fetch OT requests");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (authData?.access) {
            fetchData();
            fetchOrganisations();
        }
        if (authData?.employee?.role === 2) setAllowEdit(true);
    }, [authData]);

    // Re-apply client-side filters when status filter changes
    useEffect(() => {
        const filtered = applyClientSideFilters(data);
        setFilteredData(filtered);
        setPagination(prev => ({
            ...prev,
            current: 1,
            total: filtered.length
        }));
    }, [filters.selectedStatus, data]);

    // Apply filters - fetch new data
    const applyFilters = () => {
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchData(1, pagination.pageSize);
    };

    // Handle table pagination change
    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
    };

    // Export to Excel with all filtered data (no pagination)
    const exportToExcel = async () => {
        try {
            const queryString = buildQueryParams(1, 10000); // Get all records
            const res = await axios.get(
                `${backUrl}/api/payroll/manager/ot-requests/?${queryString}`,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );

            const rawExportData = res.data.results || res.data;

            // Apply client-side filtering to export data as well
            const exportData = applyClientSideFilters(rawExportData);

            if (!exportData.length) {
                message.warning("No data available to export");
                return;
            }

            // Sheet 1: Main OT Requests
            const mainSheetData = exportData.map((row) => ({
                Employee: row.employee?.user?.first_name + " " + row.employee?.user?.last_name,
                Code: row.employee_name,
                Organisation: row.employee?.organisation_name,
                Department: row.employee?.department_name,
                Designation: row.employee?.designation_name,
                Date: row.attendance_date,
                "OT Hours": row.requested_ot_hours,
                "OT Amount": row.ot_amount || "",
                "Employee Remarks": row.employee_remarks || "",
                Status: row.status?.toUpperCase(),
            }));

            const ws1 = XLSX.utils.json_to_sheet(mainSheetData);

            // Sheet 2: Approval Logs
            let approvalRows = [];

            exportData.forEach((record) => {
                (record.approval_logs || []).forEach((log) => {
                    approvalRows.push({
                        Employee: record.employee?.user?.first_name + " " + record.employee?.user?.last_name,
                        Date: record.attendance_date,
                        Order: log.order,
                        Approver: log.approver_name,
                        Status: log.status?.toUpperCase(),
                        Remarks: log.remarks || "",
                        Timestamp: log.timestamp
                            ? dayjs(log.timestamp).format("DD MMM YYYY, hh:mm A")
                            : "",
                    });
                });
            });

            const ws2 = XLSX.utils.json_to_sheet(approvalRows);

            // Create Workbook
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws1, "OT Requests");
            XLSX.utils.book_append_sheet(wb, ws2, "Approval Logs");

            // Download Excel
            XLSX.writeFile(wb, "OT_Requests.xlsx");
        } catch (err) {
            message.error("Failed to export data");
        }
    };

    // Organisation Change
    const handleOrgChange = (value) => {
        setFilters(prev => ({
            ...prev,
            selectedOrganisation: value,
            selectedDepartment: "",
            selectedDesignation: "",
        }));
        setDepartmentList([]);
        setDesignationList([]);
        if (value) {
            fetchDepartments(value);
        }
    };

    // Department Change
    const handleDeptChange = (value) => {
        setFilters(prev => ({
            ...prev,
            selectedDepartment: value,
            selectedDesignation: "",
        }));
        setDesignationList([]);
        if (value) {
            fetchDesignations(value);
        }
    };

    // Status button handler
    const handleStatusFilter = (status) => {
        const currentUserId = authData?.employee?.id;

        console.log("Current User ID:", currentUserId);

        // Filter data based on the clicked status
        const filtered = data.filter((record) => {
            // Find the approval log that matches the current user by approver_id
            const userApprovalLog = (record.approval_logs || []).find((log) => {
                return log.approver === currentUserId;
            });

            console.log("userApprovalLog:", userApprovalLog);

            // If no matching approval log found, exclude this record
            if (!userApprovalLog) {
                return false;
            }

            // Check if the user's approval status matches the clicked status
            const logStatus = userApprovalLog.status?.toLowerCase();
            return logStatus === status.toLowerCase();
        });

        // If clicking the same status button, clear the filter
        if (filters.selectedStatus === status) {
            setFilteredData(data);
            setFilters(prev => ({ ...prev, selectedStatus: "" }));
            setPagination(prev => ({
                ...prev,
                current: 1,
                total: data.length
            }));
        } else {
            // Set new filtered data
            setFilteredData(filtered);
            setFilters(prev => ({ ...prev, selectedStatus: status }));
            setPagination(prev => ({
                ...prev,
                current: 1,
                total: filtered.length
            }));
        }
    };

    // Date range change
    const handleDateRangeChange = (dates, dateStrings) => {
        setFilters(prev => ({
            ...prev,
            dateFrom: dateStrings[0],
            dateTo: dateStrings[1],
        }));
    };

    // Clear all filters
    const clearFilters = () => {
        setFilters({
            searchName: "",
            searchCode: "",
            dateFrom: "",
            dateTo: "",
            selectedStatus: "",
            selectedOrganisation: "",
            selectedDepartment: "",
            selectedDesignation: "",
        });
        setDepartmentList([]);
        setDesignationList([]);
        setFilteredData(data);
        setPagination(prev => ({ ...prev, current: 1, total: data.length }));
        fetchData(1, pagination.pageSize);
    };

    // Open Modal
    const openModal = (record, type) => {
        setSelectedRecord(record);
        setActionType(type);
        setRemarks("");
        setIsModalVisible(true);
    };

    // Submit Approve / Reject
    const handleSubmit = async () => {
        if (!remarks.trim()) {
            message.warning("Please enter manager remarks");
            return;
        }

        let payload = { remarks: remarks };
        if (allowEdit) {
            if (actionType === "approve") {
                if (!selectedRecord.ot_amount || Number(selectedRecord.ot_amount) <= 0) {
                    message.warning("Please enter valid OT Amount before approving");
                    return;
                }

                payload.ot_amount = selectedRecord.ot_amount;
            }
        }

        const endpoint =
            actionType === "approve"
                ? `${backUrl}/api/payroll/manager/ot-requests/${selectedRecord.id}/approve/`
                : `${backUrl}/api/payroll/manager/ot-requests/${selectedRecord.id}/reject/`;

        setSubmitLoading(true);
        try {
            await axios.post(endpoint, payload, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            toast.success(
                `OT request ${actionType === "approve" ? "approved" : "rejected"} successfully`
            );

            setIsModalVisible(false);
            fetchData(pagination.current, pagination.pageSize);
        } catch (err) {
            const errorMessage =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                "Failed to process OT request";

            toast.error(errorMessage);
        } finally {
            setSubmitLoading(false);
        }
    };

    const columns = [
        {
            title: "Employee",
            key: "employee",
            align: "center",
            render: (_, record) =>
                record.employee?.user?.first_name +
                " " +
                record.employee?.user?.last_name || "—",
        },
        {
            title: "Code",
            dataIndex: "employee_name",
            key: "code",
            align: "center",
        },

        {
            title: "Date",
            dataIndex: "attendance_date",
            align: "center",
        },
        {
            title: "First In",
            dataIndex: "in_time",
            align: "center",
        },
        {
            title: "Last Out",
            dataIndex: "out_time",
            align: "center",
        },
        {
            title: "OT Hours",
            dataIndex: "approved_ot_hours",
            align: "center",
        },
        ...(authData?.employee?.is_superuser
            ? [
                {
                    title: "OT Amount",
                    dataIndex: "ot_amount",
                    align: "center",
                }
            ]
            : []),

        {
            title: "Employee Remarks",
            dataIndex: "employee_remarks",
            align: "center",
        },
        {
            title: "Overall Status",
            dataIndex: "status",
            align: "center",
            render: (status) => {
                let color =
                    status === "approved"
                        ? "green"
                        : status === "rejected"
                            ? "red"
                            : "orange";
                return <p color={color}>{status?.toUpperCase()}</p>;
            },
        },
        {
            title: "Organisation",
            dataIndex: ["employee", "organisation_name"],
        },
        {
            title: "Department",
            dataIndex: ["employee", "department_name"],
        },
        {
            title: "Designation",
            dataIndex: ["employee", "designation_name"],
        },
        {
            title: "Approval Hierarchy",
            key: "approval_logs",
            width: 400,
            render: (_, record) => {
                const logs = record.approval_logs || [];
                if (!logs.length) return "-";

                return (
                    <Timeline style={{ marginTop: "20px" }}>
                        {logs.map((log, index) => {
                            const statusColor =
                                log.status === "APPROVED"
                                    ? "green"
                                    : log.status === "REJECTED"
                                        ? "red"
                                        : "gray";

                            return (
                                <Timeline.Item key={index} color={statusColor}>
                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                            <div>
                                                <Text strong style={{ fontSize: "14px" }}>
                                                    {log.approver_name}
                                                </Text>
                                            </div>
                                            <Tag color={statusColor}>{log.status}</Tag>
                                        </div>

                                        {/* Combined Action Details (Additive) */}
                                        <div style={{ marginTop: "6px" }}>
                                            {/* Regular Approval / Rejection Block */}
                                            {(log.timestamp || log.remarks) && (
                                                <div style={{ marginBottom: "8px" }}>
                                                    {log.timestamp && (
                                                        <div style={{ color: "#595959", fontSize: "12px", marginBottom: "2px" }}>
                                                            <Text strong style={{ fontSize: "11px", color: "#595959" }}>{log.status === "REJECTED" ? "Rejected On: " : "On: "}</Text>
                                                            {dayjs(log.timestamp).format("DD MMM YYYY [at] hh:mm A")}
                                                        </div>
                                                    )}
                                                    {log.remarks && (
                                                        <div style={{ fontSize: "13px" }}>
                                                            <Text strong>Remark: </Text>
                                                            <Text>{log.remarks}</Text>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Cancellation Block */}
                                            {(log.cancellation_approved_on || log.cancellation_rejected_on || log.cancellation_reason) && (
                                                <div style={{
                                                    marginTop: (log.timestamp || log.remarks) ? "8px" : "0",
                                                    paddingTop: (log.timestamp || log.remarks) ? "8px" : "0",
                                                    borderTop: (log.timestamp || log.remarks) ? "1px dashed #e8e8e8" : "none"
                                                }}>
                                                    {(log.cancellation_approved_on || log.cancellation_rejected_on) && (
                                                        <div style={{ color: "#595959", fontSize: "12px", marginBottom: "2px" }}>
                                                            <Text strong style={{ fontSize: "11px", color: "#595959" }}>Cancellation {log.cancellation_rejected_on ? "Rejected On: " : "Approved On: "}</Text>
                                                            {dayjs(log.cancellation_approved_on || log.cancellation_rejected_on).format("DD MMM YYYY [at] hh:mm A")}
                                                        </div>
                                                    )}
                                                    {log.cancellation_reason && (
                                                        <div style={{ fontSize: "13px" }}>
                                                            <Text strong>Cancellation Remark: </Text>
                                                            <Text>{log.cancellation_reason}</Text>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Timeline.Item>
                            );
                        })}
                    </Timeline>
                );
            },
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            render: (record) =>
                allowEdit ? (
                    <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
                        <Input
                            type="number"
                            placeholder="OT Amount"
                            style={{ width: "120px" }}
                            value={record.ot_amount}
                            onChange={(e) => {
                                record.ot_amount = e.target.value;
                            }}
                        />

                        <Button
                            shape="circle"
                            icon={<CheckOutlined />}
                            style={{ backgroundColor: "green", color: "white" }}
                            onClick={() => openModal(record, "approve")}
                        />

                        <Button
                            shape="circle"
                            icon={<CloseOutlined />}
                            danger
                            onClick={() => openModal(record, "reject")}
                        />
                    </div>
                ) : (
                    <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
                        <Button
                            shape="circle"
                            icon={<CheckOutlined />}
                            style={{ backgroundColor: "green", color: "white" }}
                            onClick={() => openModal(record, "approve")}
                        />
                        <Button
                            shape="circle"
                            icon={<CloseOutlined />}
                            danger
                            onClick={() => openModal(record, "reject")}
                        />
                    </div>
                ),
        },
    ];

    const expandedRowRender = (record) => {
        const approvalColumns = [
            {
                title: "Order",
                dataIndex: "order",
                align: "center",
            },
            {
                title: "Approver",
                dataIndex: "approver_name",
                align: "center",
            },
            {
                title: "Overall Status",
                dataIndex: "status",
                align: "center",
                render: (status) => {
                    let color =
                        status?.toLowerCase() === "approved"
                            ? "green"
                            : status?.toLowerCase() === "rejected"
                                ? "red"
                                : "orange";
                    return <Tag color={color}>{status?.toUpperCase()}</Tag>;
                },
            },
            {
                title: "Remarks",
                dataIndex: "remarks",
                align: "center",
                render: (text) => text || "—",
            },
            {
                title: "Timestamp",
                dataIndex: "timestamp",
                align: "center",
                render: (value) =>
                    value ? dayjs(value).format("DD MMM YYYY, hh:mm A") : "—",
            },
        ];

        return (
            <Table
                columns={approvalColumns}
                dataSource={record.approval_logs || []}
                rowKey="id"
                pagination={false}
                size="small"
                bordered
                style={{ marginLeft: "40px" }}
            />
        );
    };

    // Paginate filtered data client-side
    const getPaginatedData = () => {
        const startIndex = (pagination.current - 1) * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        return filteredData.slice(startIndex, endIndex);
    };

    return (
        <div style={{ margin: "30px" }}>
            {/* STATUS FILTER BUTTONS */}
            <Row justify="center" gutter={16} style={{ marginBottom: 20 }}>
                <Col>
                    <Button
                        type={filters.selectedStatus === "pending" ? "primary" : "default"}
                        onClick={() => handleStatusFilter("PENDING")}
                        style={{
                            backgroundColor: filters.selectedStatus === "pending" ? "#faad14" : undefined,
                            borderColor: filters.selectedStatus === "pending" ? "#faad14" : undefined,
                            color: filters.selectedStatus === "pending" ? "white" : undefined,
                            minWidth: "120px"
                        }}
                    >
                        Pending
                    </Button>
                </Col>
                <Col>
                    <Button
                        type={filters.selectedStatus === "approved" ? "primary" : "default"}
                        onClick={() => handleStatusFilter("APPROVED")}
                        style={{
                            backgroundColor: filters.selectedStatus === "approved" ? "#52c41a" : undefined,
                            borderColor: filters.selectedStatus === "approved" ? "#52c41a" : undefined,
                            color: filters.selectedStatus === "approved" ? "white" : undefined,
                            minWidth: "120px"
                        }}
                    >
                        Approved
                    </Button>
                </Col>
                <Col>
                    <Button
                        type={filters.selectedStatus === "rejected" ? "primary" : "default"}
                        onClick={() => handleStatusFilter("REJECTED")}
                        style={{
                            backgroundColor: filters.selectedStatus === "rejected" ? "#ff4d4f" : undefined,
                            borderColor: filters.selectedStatus === "rejected" ? "#ff4d4f" : undefined,
                            color: filters.selectedStatus === "rejected" ? "white" : undefined,
                            minWidth: "120px"
                        }}
                    >
                        Rejected
                    </Button>
                </Col>
            </Row>

            <Card style={{ marginBottom: 20, padding: 10 }}>
                <Row gutter={16}>
                    <Col span={8}>
                        <Input
                            placeholder="Search by Employee Name"
                            value={filters.searchName}
                            onChange={(e) => setFilters(prev => ({ ...prev, searchName: e.target.value }))}
                        />
                    </Col>

                    <Col span={8}>
                        <Input
                            placeholder="Search by Employee Code"
                            value={filters.searchCode}
                            onChange={(e) => setFilters(prev => ({ ...prev, searchCode: e.target.value }))}
                        />
                    </Col>

                    <Col span={8}>
                        <RangePicker
                            style={{ width: "100%" }}
                            value={filters.dateFrom && filters.dateTo ? [dayjs(filters.dateFrom), dayjs(filters.dateTo)] : null}
                            onChange={handleDateRangeChange}
                        />
                    </Col>
                </Row>

                <Row gutter={16} style={{ marginTop: 15 }}>
                    <Col span={6}>
                        <Select
                            style={{ width: "100%" }}
                            placeholder="Select Organisation"
                            value={filters.selectedOrganisation || undefined}
                            onChange={handleOrgChange}
                            allowClear
                        >
                            {organisationList.map((org) => (
                                <Option key={org.id} value={org.id}>
                                    {org.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col span={6}>
                        <Select
                            showSearch
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                            style={{ width: "100%" }}
                            placeholder="Select Department"
                            value={filters.selectedDepartment || undefined}
                            onChange={handleDeptChange}
                            disabled={!filters.selectedOrganisation}
                            allowClear
                        >
                            {departmentList.map((dept) => (
                                <Option key={dept.id} value={dept.id}>
                                    {dept.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col span={6}>
                        <Select
                            showSearch
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                            style={{ width: "100%" }}
                            placeholder="Select Designation"
                            value={filters.selectedDesignation || undefined}
                            onChange={(v) => setFilters(prev => ({ ...prev, selectedDesignation: v }))}
                            disabled={!filters.selectedDepartment}
                            allowClear
                        >
                            {designationList.map((des) => (
                                <Option key={des.id} value={des.id}>
                                    {des.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col span={6}>
                        <Button
                            type="primary"
                            onClick={applyFilters}
                            style={{ marginRight: 8, background: '#0e497b', color: 'white' }}
                        >
                            Apply Filters
                        </Button>
                        <Button onClick={clearFilters}>
                            Clear
                        </Button>
                    </Col>
                </Row>

                <Row style={{ marginTop: 15 }}>
                    <Col>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={exportToExcel}
                            style={{ backgroundColor: "#0e497b" }}
                        >
                            Export to Excel
                        </Button>
                    </Col>
                </Row>
            </Card>

            {loading ? (
                <div style={{ textAlign: "center", padding: "50px" }}>
                    <Spin size="large" />
                </div>
            ) : (
                <Table
                    columns={columns}
                    dataSource={getPaginatedData()}
                    rowKey="id"
                    bordered
                    pagination={pagination}
                    onChange={handleTableChange}
                    expandable={{ expandedRowRender }}
                    scroll={{ x: 1500 }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{
                                        backgroundColor: "#0e497b",
                                        color: "white",
                                        textAlign: "center",
                                    }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            )}

            {/* Modal */}
            <Modal
                title={actionType === "approve" ? "Approve OT Request" : "Reject OT Request"}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setIsModalVisible(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={submitLoading}
                        style={{
                            backgroundColor: actionType === "approve" ? "green" : "red",
                            color: "white",
                        }}
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>,
                ]}
            >
                <p>
                    <strong>Employee:</strong>{" "}
                    {selectedRecord?.employee_name ||
                        selectedRecord?.employee?.name}
                </p>
                <p>
                    <strong>Date:</strong> {selectedRecord?.attendance_date}
                </p>

                <TextArea
                    rows={4}
                    placeholder={`Enter manager remarks for ${actionType === "approve" ? "approval" : "rejection"
                        }`}
                    name="remarks"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                />
            </Modal>

            <ToastContainer position="top-right" autoClose={3000} />
        </div>
    );
}